/**
 * Where editors' time goes (Ryan, 2026-09-25): the one-time survey an editor answers before their board,
 * and the two questions they answer with each delivery. backend/app/editor_time.py carries the same lists
 * and checks the same rules; backend/tests/test_editor_time.py fails if the two drift apart.
 */

// [key, label]
export const TIME_SINKS = [
  ['downloading', 'Downloading or copying footage'],
  ['organizing', 'Organizing and labeling clips'],
  ['syncing_audio', 'Syncing audio'],
  ['finding_moments', 'Finding vows, speeches and toasts'],
  ['picking_music', 'Picking music'],
  ['color', 'Color'],
  ['audio_cleanup', 'Audio cleanup'],
  ['exporting', 'Exporting and uploading'],
  ['revisions', 'Revisions'],
  ['other', 'Other'],
];
export const PROGRAMS = [
  ['premiere', 'Premiere Pro'],
  ['resolve', 'DaVinci Resolve'],
  ['fcp', 'Final Cut Pro'],
  ['other', 'Other'],
];
export const TIME_BUCKETS = [
  ['under_2', 'Under 2 hrs'],
  ['2_4', '2 to 4'],
  ['4_8', '4 to 8'],
  ['8_12', '8 to 12'],
  ['12_plus', '12+'],
];

export const SURVEY_INTRO = 'Help us figure out where your time goes so we can build tools that make your job faster. This has nothing to do with your rates.';
export const SURVEY_Q = {
  hours: 'For a typical highlight film, about how many hours from download to first draft?',
  sinks: 'Which 2 parts that are NOT creative take the most time?',
  wish: 'What is one thing you wish was already done for you before you start?',
  program: 'Which editing program do you use?',
};
export const DELIVERY_Q = {
  time: 'About how long did this one take?',
  sink: 'What took the longest this time?',
  worked: 'What worked for this client?',
};

export const MAX_HOURS = 200;
export const SHORT = 200;
export const OTHER_TEXT = 120;
export const WORKED_TEXT = 300;

const filled = (s) => typeof s === 'string' && s.trim().length > 0;
const keys = (list) => list.map(([k]) => k);

/** The first thing stopping the survey from being sent, as a sentence the editor can act on; null when it can go.
 *  It NAMES the problem because the survey is blocking: "Answer all four" shown to an editor who had answered all
 *  four -- with 360 in the hours box, over the 200 limit -- left him locked out of the portal with no way to know why
 *  (Dexter Manalo, 2026-09-29). The limits are the ones the server checks (app/editor_time.py). */
export const surveyProblem = (a) => {
  const raw = a?.typical_hours;
  const hours = Number(raw);
  // Rounded to a tenth before the range checks, as the server rounds before it checks (app/editor_time.py): 0.04
  // would otherwise enable the button for a number the server refuses as 0.
  const tenths = Math.round(hours * 10) / 10;
  const sinks = a?.time_sinks || [];
  // A number box that holds text it cannot read ("6-8", "8 hrs", "3,5" in some browsers) reports its value as
  // empty, so without this the hint said "enter the hours" to an editor looking at the hours they had entered:
  // the same trap as the 360 above. EditorTimeSurvey sets hours_unreadable from the box's validity.badInput.
  if (a?.hours_unreadable) return 'Question 1: type one number, like 8 or 8.5. A range or a word cannot be read.';
  if (raw === '' || raw === undefined || raw === null) return 'Question 1: enter the hours.';
  if (!Number.isFinite(hours) || tenths <= 0) return 'Question 1: enter the hours as a number above 0.';
  if (tenths > MAX_HOURS) return `Question 1: ${hours} is more than ${MAX_HOURS} hours. Enter the hours for ONE highlight film, from download to first draft.`;
  // One of two picked is the easy one to miss (Ryan, 2026-09-30, doing it himself): say how many are still owed.
  if (sinks.length === 1) return 'Question 2: pick one more. It asks for two, and you have picked one.';
  if (sinks.length !== 2 || new Set(sinks).size !== 2 || !sinks.every(s => keys(TIME_SINKS).includes(s))) return 'Question 2: pick two.';
  if (sinks.includes('other') && !filled(a?.time_sinks_other)) return 'Question 2: say what "Other" is.';
  if (!filled(a?.wish)) return 'Question 3: write one thing.';
  if (a.wish.trim().length > SHORT) return `Question 3: keep it under ${SHORT} characters.`;
  if (!keys(PROGRAMS).includes(a?.program)) return 'Question 4: pick your editing program.';
  if (a.program === 'other' && !filled(a?.program_other)) return 'Question 4: say which program.';
  return null;
};

/** Which question (1-4) a surveyProblem sentence is about, so the card can take the editor to it. */
export const problemQuestion = (problem) => {
  const m = /^Question (\d)\b/.exec(problem || '');
  return m ? Number(m[1]) : null;
};

/** Every survey answer present and within the rules the server checks: exactly when there is no problem. */
export const surveyComplete = (a) => surveyProblem(a) === null;

export const EMPTY_DELIVERY = { time_bucket: '', time_sink: '', time_sink_other: '', what_worked: '' };

/** The two required delivery answers present. The line about what worked is optional. */
export const deliveryComplete = (a) =>
  keys(TIME_BUCKETS).includes(a?.time_bucket) && keys(TIME_SINKS).includes(a?.time_sink)
  && (a.time_sink !== 'other' || filled(a?.time_sink_other));

/** What a deliver request carries, trimmed. */
export const deliveryPayload = (a) => ({
  time_bucket: a.time_bucket,
  time_sink: a.time_sink,
  time_sink_other: a.time_sink === 'other' ? (a.time_sink_other || '').trim() : '',
  what_worked: (a.what_worked || '').trim(),
});
